import Vue from 'vue';
import Router from 'vue-router';
import LoginView from './views/LoginView.vue';
import RedirectToTenantStartView from '@/views/RedirectToTenantStartView';
import UserService from './service/user.service';
import TenantService from './service/tenant.service';
import { ROLES } from './config/roles';
import { waitForScrollMemory } from '@/helpers/scrollMemory';
import { rememberLoginReturn } from '@/helpers/loginReturn';

// Admin views
import UserManagementView from './views/UserManagementView.vue';
import Tenants from './views/TenantsView.vue';

// User views
import UserProfile from './views/UserProfile.vue';

// Tenant views
import TenantContainer from './views/TenantContainer.vue';
import TenantUserManagementView from './views/tenant/WorkerManagementView.vue';
import WorkerView from '@/views/tenant/WorkerView';
import TenantManagementView from './views/tenant/TenantManagementView.vue';

// App specific views
import SiteView from '@/views/tenant/SiteView';
import SiteDaybookView from '@/views/tenant/SiteDaybookView';
import EquipmentsView from '@/views/tenant/EquipmentsView';
import ReportView from '@/views/tenant/ReportView';
import TimerView from '@/views/tenant/TimerView';
import ItemsView from '@/views/tenant/ItemsView';
import InventoryView from '@/views/tenant/InventoryView';
import ProjectsView from '@/views/tenant/ProjectsView';
import ProjectView from '@/views/tenant/ProjectView';
import QuotationsView from '@/views/tenant/QuotationsView';
import QuotationView from '@/views/tenant/QuotationView';
import PartiesView from '@/views/tenant/PartiesView';
import PartyView from '@/views/tenant/PartyView';

import PersonsView from '@/views/tenant/PersonsView';
import PersonView from '@/views/tenant/PersonView';

import WorkSitesView from '@/views/tenant/WorkSitesView';
import WorkerLicensesView from '@/views/tenant/WorkerLicensesView';

import WorkGroupsView from '@/views/tenant/WorkGroupsView';
import WorkMethodsView from '@/views/tenant/WorkMethodsView';
import ItemTypesView from '@/views/tenant/ItemTypesView';
import WorkTasksView from '@/views/tenant/WorkTaskView';
import TimerAdminView from '@/views/tenant/TimerAdminView';

Vue.use(Router);

const ALIURAKOITSIJA_ALLOWED_ROUTES = ['login', 'start', 'userprofile', 'tenant_timer', 'tenants'];

function isOnlyAliurakoitsija() {
  const userInfo = UserService.getUserInfo();
  if (!userInfo || !userInfo.tenantRoles) return false;
  const tenantId = TenantService.tenant;
  const tenantEntry = userInfo.tenantRoles.find(t => t.tenant === tenantId);
  if (!tenantEntry) return false;
  const roles = tenantEntry.roleSet || [];
  const hasAliurakoitsija = roles.some(r => r.toLowerCase() === ROLES.TENANT_ALIURAKOITSIJA);
  const hasOtherTenantRole = roles.some(r => [ROLES.TENANT_ADMIN, ROLES.TENANT_BASIC, ROLES.TENANT_MANAGER].includes(r.toLowerCase()));
  return hasAliurakoitsija && !hasOtherTenantRole;
}

const router = new Router({
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
    },
    {
      path: '/',
      name: 'start',
      component: RedirectToTenantStartView,
    },
    {
      path: '/users',
      name: 'users',
      component: UserManagementView,
    },
    {
      path: '/me',
      name: 'userprofile',
      component: UserProfile,
    },
    {
      path: '/t/:tenantId',
      props: true,
      component: TenantContainer,
      children: [
        {
          path: 'workers',
          name: 'tenant_workers',
          props: true,
          component: TenantUserManagementView,
        },
        {
          path: 'worker/:id',
          name: 'tenant_edit_worker',
          props: true,
          component: WorkerView,
        },
        {
          path: 'worker',
          name: 'tenant_new_worker',
          component: WorkerView,
        },
        {
          path: 'settings',
          name: 'tenant_settings',
          props: true,
          component: TenantManagementView,
        },
        {
          path: 'site/:id/:tab?',
          name: 'tenant_site_edit_site',
          props: true,
          component: SiteView,
        },
        {
          path: 'site',
          name: 'tenant_site_new_site',
          component: SiteView,
        },
        {
          path: 'site/:siteId/daybook',
          name: 'tenant_site_new_daybook',
          props: true,
          component: SiteDaybookView,
        },
        {
          path: 'site/:siteId/daybook/:id',
          name: 'tenant_site_daybook',
          props: true,
          component: SiteDaybookView,
        },
        {
          path: 'projects',
          name: 'tenant_projects',
          props: true,
          component: ProjectsView,
        },
        {
          path: 'workerlicenses',
          name: 'tenant_worker_licenses',
          props: true,
          component: WorkerLicensesView,
        },
        {
          path: 'project/:id',
          name: 'tenant_project_edit_project',
          props: true,
          component: ProjectView,
        },
        {
          path: 'project',
          name: 'tenant_project_new_project',
          component: ProjectView,
        },
        {
          path: 'quotations',
          name: 'tenant_quotations',
          component: QuotationsView,
        },
        {
          path: 'quotation',
          name: 'tenant_quotation_new',
          component: QuotationView,
        },
        {
          path: 'quotation/:id/:tab?',
          name: 'tenant_quotation_edit',
          props: true,
          component: QuotationView,
        },
        {
          path: 'persons',
          name: 'tenant_persons',
          component: PersonsView,
        },
        {
          path: 'person',
          name: 'tenant_new_person',
          props: true,
          component: PersonView,
        },
        {
          path: 'person/:id',
          name: 'tenant_person',
          props: true,
          component: PersonView,
        },
        {
          path: 'parties',
          name: 'tenant_parties',
          component: PartiesView,
        },
        {
          path: 'party',
          name: 'tenant_new_party',
          props: true,
          component: PartyView,
        },
        {
          path: 'party/:id/:tab?',
          name: 'tenant_party',
          props: true,
          component: PartyView,
        },
        {
          path: 'items',
          name: 'tenant_items',
          component: ItemsView,
        },
        {
          path: 'inventory',
          name: 'tenant_inventory',
          component: InventoryView,
        },
        {
          path: 'equipments',
          name: 'tenant_equipments',
          component: EquipmentsView,
        },
        {
          path: 'report',
          name: 'tenant_report',
          component: ReportView,
        },
        {
          path: 'timer/:id?',
          name: 'tenant_timer',
          props: { key: 'timer' },
          component: TimerView,
        },
        {
          path: 'timer_admin/:id?',
          name: 'tenant_timer_admin',
          props: { adminMode: true, key: 'timer_admin' },
          component: TimerAdminView,
        },
        {
          path: 'worksites',
          name: 'tenant_worksites',
          component: WorkSitesView,
        },
        {
          path: 'workgroups',
          name: 'tenant_workgroups',
          component: WorkGroupsView,
        },
        {
          path: 'workmethods',
          name: 'tenant_work_methods',
          component: WorkMethodsView,
        },
        {
          path: 'itemtypes',
          name: 'tenant_item_types',
          component: ItemTypesView,
        },
        {
          path: 'worktasks',
          name: 'tenant_work_tasks_view',
          component: WorkTasksView,
        },
      ],
    },
    {
      path: '/tenants',
      name: 'tenants',
      component: Tenants,
    },
    {
      path: '*',
      redirect: '/',
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // Saman näkymän sisäiset muutokset (suodattimet, sivutus, välilehdet) eivät scrollaa
    if (to.name === from.name) return false;
    // Takaisin/eteenpäin: rekisteröity sivu (ks. helpers/scrollMemory) odottaa dataansa, muut palautetaan heti
    if (savedPosition) return waitForScrollMemory(to.fullPath).then(() => savedPosition);
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  // Hash on vielä lähtösivun osoite, joten kirjautumisen jälkeen voidaan palata siihen
  if (to.name === 'login') rememberLoginReturn();
  if (isOnlyAliurakoitsija() && !ALIURAKOITSIJA_ALLOWED_ROUTES.includes(to.name)) {
    next({ name: 'tenant_timer', params: { tenantId: TenantService.tenant } });
    return;
  }
  next();
});

export default router;
